import { Transform, Type } from 'class-transformer';
import {
	ArrayMaxSize,
	IsArray,
	IsBoolean,
	IsIn,
	IsInt,
	IsISO8601,
	IsOptional,
	IsPositive,
	IsString,
	IsUUID,
	Length,
	Matches,
	Max,
	MaxLength,
	Min,
	ValidateIf,
} from 'class-validator';

const trim = ({ value }: { value: unknown }) => (typeof value === 'string' ? value.trim() : value);

export const COURSES_TAG_KEY_RE = /^[a-z][a-z0-9_]{1,48}$/;

export const COURSES_EVENT_KEY_PATTERN = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
export const COURSES_PAYLOAD_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
export const COURSES_EVENT_KEY_RULE =
	'Use domain.action in lowercase snake_case, e.g. lesson.resource_downloaded';
export const COURSES_PAYLOAD_KEY_RULE =
	'Payload fields must be lowercase snake_case, e.g. lesson_id';

export const COURSES_ACTIVE_USERS_MAX_DAYS = 365;

export const COURSES_SITE_VIDEO_KEYS = ['dashboard', 'welcome_popup'] as const;
export type CoursesSiteVideoKey = (typeof COURSES_SITE_VIDEO_KEYS)[number];

export class CoursesAdminCourseAnalyticsQueryDto {
	@IsUUID()
	courseId: string;
}

export class CoursesAdminActiveUsersQueryDto {
	/** The default (30) is applied in the service. */
	@IsOptional()
	@Transform(({ value }) =>
		value === undefined || value === null || value === '' ? undefined : Number(value)
	)
	@IsInt()
	@Min(1)
	@Max(COURSES_ACTIVE_USERS_MAX_DAYS)
	days?: number;

	@IsOptional()
	@Transform(({ value }) => (value === '' || value === 'null' ? null : value))
	@IsUUID()
	courseId?: string | null;
}

export class CoursesAdminEventFeedQueryDto {
	@IsOptional()
	@IsString()
	@MaxLength(50)
	source?: string;

	@IsOptional()
	@Matches(/^\d{4}-\d{2}-\d{2}$/)
	from?: string;

	@IsOptional()
	@Matches(/^\d{4}-\d{2}-\d{2}$/)
	to?: string;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	user?: string;

	@IsOptional()
	@IsString()
	@MaxLength(50)
	program?: string;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	school?: string;

	@IsOptional()
	@IsString()
	@MaxLength(40)
	cursor?: string;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(10)
	@Max(500)
	limit?: number;
}

export class CoursesAdminUpdateSiteVideoDto {
	@IsIn(COURSES_SITE_VIDEO_KEYS)
	key: CoursesSiteVideoKey;

	/** Trimmed; an empty string becomes `null`. Must be present (string or null). */
	@Transform(({ value }) => (typeof value === 'string' ? value.trim() || null : value))
	@ValidateIf((o: CoursesAdminUpdateSiteVideoDto) => o.youtube_url !== null)
	@IsString()
	@MaxLength(500)
	@Matches(/^https?:\/\//i, { message: 'Must be a valid URL' })
	youtube_url: string | null;

	@IsBoolean()
	active: boolean;
}

export class CoursesAdminCreateAppTagDto {
	@Transform(trim)
	@IsString()
	@Length(1, 80)
	label: string;
}

export class CoursesAdminAppTagKeyParamDto {
	@Transform(trim)
	@IsString()
	@Matches(COURSES_TAG_KEY_RE)
	key: string;
}

export class CoursesAdminCreateTagInviteDto {
	@IsString()
	@Matches(COURSES_TAG_KEY_RE, { message: 'Invalid tag' })
	tag: string;

	@IsOptional()
	@IsInt()
	@IsPositive()
	@Max(100000)
	maxUses?: number | null;

	@IsOptional()
	@IsISO8601()
	expiresAt?: string | null;
}

export class CoursesAdminSetTagInviteEnabledDto {
	@IsBoolean()
	enabled: boolean;
}

export class CoursesAdminSaveEventDefDto {
	@IsOptional()
	@IsUUID()
	id?: string;

	@IsString()
	@MaxLength(80)
	@Matches(COURSES_EVENT_KEY_PATTERN, { message: COURSES_EVENT_KEY_RULE })
	key: string;

	@IsString()
	@Length(1, 120)
	label: string;

	@IsOptional()
	@IsString()
	@MaxLength(500)
	description?: string | null;

	@IsString()
	@Length(1, 40)
	category: string;

	@IsArray()
	@ArrayMaxSize(25)
	@IsString({ each: true })
	@MaxLength(60, { each: true })
	@Matches(COURSES_PAYLOAD_KEY_PATTERN, { each: true, message: COURSES_PAYLOAD_KEY_RULE })
	payload_keys: string[];

	@IsArray()
	@ArrayMaxSize(25)
	@IsString({ each: true })
	@MaxLength(60, { each: true })
	@Matches(COURSES_PAYLOAD_KEY_PATTERN, { each: true, message: COURSES_PAYLOAD_KEY_RULE })
	required_keys: string[];

	@IsBoolean()
	active: boolean;
}
